import React from "react";
import {
  ImageOff, Package, Sprout, Snowflake, Droplet, Star, Cloud, Coffee,
  CirclePlus, Martini, Flame, Shirt, Cigarette, Layers,
} from "lucide-react";
import "../../styles/category-placeholders.css";

/*
 * Placeholders por categoría — Figma `Club_CategoryPlaceholder` (set 3722:36025).
 *
 * Se usan cuando un producto NO tiene foto. Reconstruidos en CSS + iconos de
 * lucide en vez de exportar los 42 SVG del set (14 categorías × 3 tamaños):
 * la ilustración es un degradado, un círculo blanco, un icono y una etiqueta,
 * así que en CSS pesa nada y escala a cualquier tamaño. Lo único que no se
 * reproduce es la curva decorativa del fondo, que es casi imperceptible.
 *
 * POR QUÉ HAY UNA TABLA DE SINÓNIMOS
 * El backend guarda `Category.name` como texto libre por club: no hay catálogo
 * ni validación (models.py → class Category). Cada club teclea lo que quiere, así
 * que NO se puede casar por igualdad. Se normaliza (minúsculas, sin acentos, sin
 * separadores) y se busca en `SYNONYMS`; lo que no encaje cae en `default`.
 *
 * Las dos caídas vienen del propio diseño:
 *   `none`    → el producto no tiene categoría (el "Uncategorized" del backend)
 *   `default` → tiene categoría pero no tenemos ilustración para ella
 */

// Paleta leída de Figma. Cada entrada: degradado de fondo + color de acento.
export const CATEGORY_TONES = {
  none:          { from: "#f4f4f5", to: "#e4e4e7", accent: "#52525b", label: "Sin categoría",         Icon: ImageOff },
  default:       { from: "#fff7ed", to: "#ffedd5", accent: "#ea580c", label: "Categoría por defecto", Icon: Package },
  weed:          { from: "#f0fdf4", to: "#dcfce7", accent: "#16a34a", label: "Indoor Weed",           Icon: Sprout },
  drysift:       { from: "#fffbeb", to: "#fde68a", accent: "#d97706", label: "Dry-sift",              Icon: Snowflake },
  cbd:           { from: "#f0fdfa", to: "#ccfbf1", accent: "#0d9488", label: "CBD",                   Icon: Droplet },
  especiales:    { from: "#faf5ff", to: "#f3e8ff", accent: "#9333ea", label: "Especiales",            Icon: Star },
  vaporizadores: { from: "#f0f9ff", to: "#e0f2fe", accent: "#0284c7", label: "Vaporizadores",         Icon: Cloud },
  cafeteria:     { from: "#f5ede1", to: "#eaddc7", accent: "#b45309", label: "Cafetería",             Icon: Coffee },
  extra:         { from: "#eef2ff", to: "#e0e7ff", accent: "#4f46e5", label: "Extra",                 Icon: CirclePlus },
  bar:           { from: "#fff1f2", to: "#ffe4e6", accent: "#be123c", label: "Bar",                   Icon: Martini },
  parafernalia:  { from: "#f8fafc", to: "#e2e8f0", accent: "#475569", label: "Parafernalia",          Icon: Flame },
  merchandising: { from: "#fdf4ff", to: "#fae8ff", accent: "#c026d3", label: "Merchandising",         Icon: Shirt },
  prerolls:      { from: "#f7fee7", to: "#ecfccb", accent: "#65a30d", label: "Pre-rolls",             Icon: Cigarette },
  hachis:        { from: "#efe6d5", to: "#e0cfae", accent: "#78350f", label: "Hachís",                Icon: Layers },
};

// Nombres que hemos visto o que es razonable esperar de un club, por clave.
// Se comparan YA normalizados, así que aquí no hacen falta tildes ni mayúsculas.
const SYNONYMS = {
  weed:          ["weed", "indoorweed", "indoor", "outdoor", "flores", "flor", "flower", "flowers", "marihuana", "maria", "hierba", "cogollo", "cogollos"],
  hachis:        ["hachis", "hash", "hasch", "hashish", "resina"],
  drysift:       ["drysift", "dry", "polen", "pollen", "kief", "tamizado"],
  prerolls:      ["prerolls", "preroll", "porros", "porro", "canutos", "joints", "joint"],
  cbd:           ["cbd", "cannabidiol"],
  vaporizadores: ["vaporizadores", "vaporizador", "vapes", "vape", "vaper", "cartuchos"],
  especiales:    ["especiales", "especial", "premium", "exclusivos"],
  extra:         ["extra", "extras", "otros", "varios", "miscelanea"],
  parafernalia:  ["parafernalia", "accesorios", "accesorio", "papel", "grinders", "grinder"],
  merchandising: ["merchandising", "merch", "ropa", "textil", "camisetas"],
  bar:           ["bar", "barra", "bebidas", "bebida", "refrescos", "alcohol"],
  cafeteria:     ["cafeteria", "cafe", "cafes", "coffee", "infusiones"],
};

// "Hachís" → "hachis", "Pre-Rolls" → "prerolls", "DRY SIFT" → "drysift"
export function normalizeCategory(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // fuera acentos
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");        // fuera espacios, guiones y signos
}

const LOOKUP = Object.entries(SYNONYMS).reduce((acc, [key, names]) => {
  names.forEach((n) => { acc[n] = key; });
  return acc;
}, {});

/** Nombre de categoría del backend → clave de ilustración. */
export function categoryToneKey(name) {
  const n = normalizeCategory(name);
  if (!n || n === "uncategorized" || n === "sincategoria") return "none";
  return LOOKUP[n] || "default";
}

export function getCategoryTone(name) {
  return CATEGORY_TONES[categoryToneKey(name)];
}

/**
 * Placeholder de un producto sin foto.
 * size: "lg" muestra la etiqueta; "md" y "sm" solo el icono, como en Figma.
 */
export function CategoryPlaceholder({ category, size = "lg", className = "" }) {
  const tone = getCategoryTone(category);
  const { Icon } = tone;
  const iconSize = size === "sm" ? 16 : size === "md" ? 28 : 32;

  return (
    <div
      className={`cph cph--${size}${className ? ` ${className}` : ""}`}
      style={{ "--cph-from": tone.from, "--cph-to": tone.to, "--cph-accent": tone.accent }}
      role="img"
      aria-label={tone.label}
    >
      <span className="cph-chip"><Icon size={iconSize} strokeWidth={1.6} /></span>
      {size === "lg" && <span className="cph-label">{tone.label}</span>}
    </div>
  );
}
